"use client";
import { cn } from "@/lib/utils";
import { IconMenu2, IconX } from "@tabler/icons-react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "motion/react";

import React, { useRef, useState, useEffect } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { getLocalizedUrl } from "@/utils/languageUtils";
import { trackCTAClick, trackBookingIntent } from "@/utils/analytics";

interface NavbarProps {
  children: React.ReactNode;
  className?: string;
  isBookingPage?: boolean;
}

interface NavBodyProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
  /** When true, nav uses dark text and frosted bar from first paint (e.g. white page hero). */
  lightBackground?: boolean;
  isBookingPage?: boolean;
  onMenuToggle?: () => void;
  isMenuOpen?: boolean;
}

interface NavItemsProps {
  items: {
    name: string;
    link: string;
  }[];
  className?: string;
  onItemClick?: () => void;
  visible?: boolean;
  lightBackground?: boolean;
}

interface MobileNavProps {
  children: React.ReactNode;
  className?: string;
  visible?: boolean;
  isBookingPage?: boolean;
}

interface MobileNavHeaderProps {
  children: React.ReactNode;
  className?: string;
  isBookingPage?: boolean;
}

interface MobileNavMenuProps {
  children: React.ReactNode;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  isBookingPage?: boolean;
}

export const Navbar = ({ children, className, isBookingPage }: NavbarProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();
  const [visible, setVisible] = useState<boolean>(false);
  const [hasScrolled, setHasScrolled] = useState(false);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > 20) {
      setVisible(true);
      if (isBookingPage) setHasScrolled(true);
    } else {
      if (!isBookingPage) setVisible(false);
    }
  });

  const effectiveVisible = isBookingPage ? (hasScrolled || visible) : visible;

  return (
    <motion.div
      ref={ref}
      className={cn(
        "fixed inset-x-0 lg:top-[36px] lg:bottom-auto z-[60] w-full",
        isBookingPage 
          ? "top-0 bottom-auto" // Top position for booking page on mobile
          : "bottom-0 top-auto", // Bottom position for other pages on mobile
        className,
      )}
      data-oid="d4.t5y1"
    >
      <div className="w-full h-full relative px-4">
        {React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(
                child as React.ReactElement<{ visible?: boolean; isBookingPage?: boolean }>,
                { visible: effectiveVisible, isBookingPage },
              )
            : child,
        )}
      </div>
    </motion.div>
  );
};

export const NavBody = ({ 
  children, 
  className, 
  visible, 
  lightBackground,
  isBookingPage,
  onMenuToggle,
  isMenuOpen 
}: NavBodyProps) => {
  const isMinimized = isBookingPage && visible;
  const barVisible = visible || lightBackground;

  return (
    <motion.div
      layout
      animate={{
        backdropFilter: barVisible ? "blur(10px)" : "none",
        boxShadow: barVisible
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "none",
        width: isMinimized ? "fit-content" : (barVisible ? "90%" : "100%"),
        y: barVisible ? (isBookingPage ? 0 : 6) : 0, // Remove y-offset on booking page to keep it higher
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      className={cn(
        "relative z-[60] hidden lg:grid grid-cols-[auto_1fr_auto] items-center rounded-full bg-transparent px-4 py-2 dark:bg-transparent",
        barVisible && "bg-white/80 dark:bg-neutral-950/80",
        isMinimized ? "ml-auto mr-0" : "mx-auto",
        !isMinimized && "lg:min-w-[800px]",
        className,
      )}
      data-oid="v54mm0h"
    >
      {isMinimized ? (
        <div className="flex items-center gap-6 px-2">
          <NavbarLogo />
          <div className="h-4 w-px bg-gray-200" />
          <MobileNavToggle isOpen={isMenuOpen ?? false} onClick={onMenuToggle ?? (() => {})} />
        </div>
      ) : (
        React.Children.map(children, (child) =>
          React.isValidElement(child)
            ? React.cloneElement(
                child as React.ReactElement<{ visible?: boolean }>,
                { visible: barVisible },
              )
            : child,
        )
      )}
    </motion.div>
  );
};

export const NavItems = ({
  items,
  className,
  onItemClick,
  visible,
  lightBackground,
}: NavItemsProps) => {
  const [hovered, setHovered] = useState<number | null>(null);
  const { language } = useLanguage();

  return (
    <motion.div
      onMouseLeave={() => setHovered(null)}
      className={cn(
        "hidden col-start-2 col-end-3 flex flex-row items-center justify-center space-x-4 text-base transition duration-200 lg:flex lg:space-x-4",
        visible || lightBackground
          ? "text-gray-900 hover:text-alpine-gold"
          : "text-white hover:text-alpine-gold",
        className,
      )}
      data-oid="d79uzhs"
    >
      {items.map((item, idx) => (
        <a
          onMouseEnter={() => setHovered(idx)}
          onClick={onItemClick}
          className={cn(
            "relative px-4 py-2 transition-colors duration-200",
            visible || lightBackground
              ? "text-gray-900 hover:text-alpine-gold"
              : "text-white hover:text-alpine-gold",
          )}
          key={`link-${idx}`}
          href={getLocalizedUrl(item.link, language)}
          data-oid="3517u07"
        >
          {hovered === idx && (
            <motion.div
              layoutId="hovered"
              className="absolute inset-0 h-full w-full rounded-full bg-gray-100 dark:bg-neutral-800"
              data-oid="4750e.p"
            />
          )}
          <span className="relative z-20" data-oid="wzyx-pg">
            {item.name}
          </span>
        </a>
      ))}
    </motion.div>
  );
};

export const MobileNav = ({ children, className, visible, isBookingPage }: MobileNavProps) => {
  // Use visible state to control mobile nav shrinking
  const showNav = visible;

  return (
    <motion.div
      animate={{
        backdropFilter: !isBookingPage && showNav ? "blur(10px)" : "none",
        boxShadow: !isBookingPage && showNav
          ? "0 0 24px rgba(34, 42, 53, 0.06), 0 1px 1px rgba(0, 0, 0, 0.05), 0 0 0 1px rgba(34, 42, 53, 0.04), 0 0 4px rgba(34, 42, 53, 0.08), 0 16px 68px rgba(47, 48, 55, 0.05), 0 1px 0 rgba(255, 255, 255, 0.1) inset"
          : "0 -2px 10px rgba(0, 0, 0, 0.15)",
        width: isBookingPage ? "auto" : showNav ? "90%" : "100%",
        paddingRight: isBookingPage ? "0px" : showNav ? "12px" : "0px",
        paddingLeft: isBookingPage ? "0px" : showNav ? "12px" : "0px",
        borderRadius: isBookingPage ? "9999px" : showNav ? "4px" : "2rem",
        y: showNav ? -6 : 0,
      }}
      transition={{
        type: "spring",
        stiffness: 200,
        damping: 50,
      }}
      className={cn(
        "relative z-50 flex flex-col items-center justify-between px-0 py-2 lg:hidden",
        isBookingPage
          ? "ml-auto mr-4 mt-4 w-auto bg-transparent" // top-right minimal hamburger on booking page
          : "mx-auto w-full max-w-full bg-white/80 dark:bg-neutral-950/80",
        className,
      )}
      data-oid="gnzh27v"
    >
      {children}
    </motion.div>
  );
};

export const MobileNavHeader = ({
  children,
  className,
  isBookingPage,
}: MobileNavHeaderProps) => {
  return (
    <div
      className={cn(
        "flex flex-row items-center",
        isBookingPage ? "justify-end w-auto" : "justify-between w-full",
        className,
      )}
      data-oid="4bfc493"
    >
      {children}
    </div>
  );
};

export const MobileNavMenu = ({
  children,
  className,
  isOpen,
  onClose,
  isBookingPage,
}: MobileNavMenuProps) => {
  return (
    <AnimatePresence data-oid="3i:wtw0">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={cn(
            "absolute z-50 flex flex-col items-start justify-start gap-4 rounded-lg bg-white px-4 py-8 shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset] dark:bg-neutral-950",
            isBookingPage
              ? "top-16 right-4 w-80" // Top right positioning for booking page
              : "inset-x-0 bottom-16 lg:top-16 lg:bottom-auto w-full", // Full width for other pages
            className,
          )}
          data-oid="9t2v8yt"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const MobileNavToggle = ({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) => {
  return isOpen ? (
    <IconX
      className="text-neutral-600 dark:text-neutral-300 cursor-pointer"
      onClick={onClick}
      data-oid="wi.vvrt"
    />
  ) : (
    <IconMenu2
      className="text-neutral-600 dark:text-neutral-300 cursor-pointer"
      onClick={onClick}
      data-oid="61r3u08"
    />
  );
};

export const NavbarLogo = () => {
  const { language } = useLanguage();

  return (
    <a
      href={getLocalizedUrl("/", language)}
      className="relative z-20 mr-4 flex items-center px-2 py-1 text-sm font-medium text-black"
      data-oid="11patgg"
    >
      <img
        src="/images/updated logo.svg"
        alt="Beatenberg Resort & Spa"
        width={100}
        height={50}
        className="h-8 w-auto object-contain"
        data-oid="ejs43he"
      />
    </a>
  );
};

export const LanguageSelector = ({
  visible,
  lightBackground,
  /** Icon + chevron only (e.g. narrow booking bar); saves horizontal space. */
  compact,
  /** Accessible name when `compact` hides the visible language code. */
  compactAriaLabel = "Language",
}: {
  visible?: boolean;
  lightBackground?: boolean;
  compact?: boolean;
  compactAriaLabel?: string;
}) => {
  const { language, setLanguage, getLanguageLabel } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages: { code: Language; label: string; name: string }[] = [
    { code: "en", label: "EN", name: "English" },
    { code: "de", label: "DE", name: "Deutsch" },
    { code: "fr", label: "FR", name: "Français" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef} data-oid="ply9yxz">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={compact ? compactAriaLabel : undefined}
        className={cn(
          "flex items-center transition-colors duration-300",
          compact ? "gap-0.5 rounded-md p-1" : "space-x-2",
          visible || lightBackground
            ? "text-gray-900 hover:text-alpine-gold"
            : "text-white hover:text-alpine-gold",
        )}
        data-oid="tj5ju.a"
      >
        <Globe size={compact ? 16 : 18} data-oid="66jxspu" />
        {!compact ? (
          <span className="font-lato text-sm" data-oid="6.az9un">
            {getLanguageLabel(language)}
          </span>
        ) : null}
        <ChevronDown
          size={compact ? 12 : 14}
          className={cn(
            "transition-transform duration-200 shrink-0",
            isOpen && "rotate-180",
          )}
          data-oid="b9:0ywn"
        />
      </button>

      <AnimatePresence data-oid="ju-1tvp">
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-0 mt-2 w-32 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
            data-oid="ek.el7l"
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={cn(
                  "w-full px-3 py-2 text-left text-sm font-lato transition-colors duration-200",
                  language === lang.code
                    ? "bg-alpine-gold text-white"
                    : "text-gray-700 hover:bg-gray-100",
                )}
                data-oid="1mnkhm2"
              >
                <div
                  className="flex items-center justify-between"
                  data-oid="lhqv-ny"
                >
                  <span className="font-semibold" data-oid="c:e5r8y">
                    {lang.label}
                  </span>
                  <span className="text-xs opacity-75" data-oid="fcpemd7">
                    {lang.name}
                  </span>
                </div>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const NavRightSection = ({
  children,
  visible,
}: {
  children: React.ReactNode;
  visible?: boolean;
}) => {
  return (
    <div
      className="flex items-center gap-4 justify-self-end"
      data-oid="0dpvdtb"
    >
      {React.Children.map(children, (child) =>
        React.isValidElement(child)
          ? React.cloneElement(
              child as React.ReactElement<{ visible?: boolean }>,
              { visible },
            )
          : child,
      )}
    </div>
  );
};

export const NavbarButton = ({
  href,
  as: Tag = "a",
  children,
  className,
  variant = "primary",
  visible,
  trackingType = "none",
  trackingLabel,
  trackingLocation,
  onClick,
  ...props
}: {
  href?: string;
  as?: React.ElementType;
  children: React.ReactNode;
  className?: string;
  variant?: "primary" | "secondary" | "dark" | "gradient";
  visible?: boolean;
  trackingType?: "cta" | "booking" | "none";
  trackingLabel?: string;
  trackingLocation?: string;
  onClick?: any;
} & (
  | React.ComponentPropsWithoutRef<"a">
  | React.ComponentPropsWithoutRef<"button">
)) => {
  const baseStyles =
    "px-4 py-2 rounded-full bg-white button bg-white text-black text-sm font-extrabold relative cursor-pointer hover:-translate-y-0.5 transition duration-200 inline-block text-center";

  const variantStyles = {
    primary:
      "bg-alpine-gold hover:bg-alpine-gold/90 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105",
    secondary:
      "bg-transparent shadow-none text-gray-900 hover:text-alpine-gold dark:text-white",
    dark: "bg-pine-green text-white shadow-[0_0_24px_rgba(34,_42,_53,_0.06),_0_1px_1px_rgba(0,_0,_0,_0.05),_0_0_0_1px_rgba(34,_42,_53,_0.04),_0_0_4px_rgba(34,_42,_53,_0.08),_0_16px_68px_rgba(47,_48,_55,_0.05),_0_1px_0_rgba(255,_255,_255,_0.1)_inset]",
    gradient:
      "bg-gradient-to-b from-alpine-gold to-alpine-gold/80 text-white shadow-[0px_2px_0px_0px_rgba(255,255,255,0.3)_inset]",
  };

  // Handle click with analytics tracking
  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    // Track analytics based on tracking type
    if (trackingType === "cta" && trackingLabel) {
      trackCTAClick(trackingLabel, trackingLocation);
    } else if (trackingType === "booking") {
      trackBookingIntent(undefined, trackingLabel, trackingLocation);
    }

    // Call original onClick if provided
    if (onClick) {
      onClick(e as any);
    }
  };

  // Determine the component type based on href
  const Component = href ? "a" : "button";

  // Filter out visible and tracking props to prevent them from being passed to DOM
  const { visible: _, ...filteredProps } = props as any;

  return (
    <Component
      href={href}
      className={cn(baseStyles, variantStyles[variant], className)}
      onClick={handleClick}
      {...filteredProps}
      data-oid="uihj0w6"
    >
      {children}
    </Component>
  );
};
